/**
 * <TutorFilmstrip> — la pellicule, la colonne de gauche du live du masteur (organism).
 *
 * La piste B du round 2, validée par Wajih le 02/11 : les visages à gauche, une vignette vidéo par
 * élève (`<TutorStudentTile>`), dans l'ordre du roster, et **la mienne en bas, toujours visible**, à la
 * même largeur (« Tu présentes » quand je présente, « Écran partagé » quand je partage mon écran).
 *
 * L'élève regardé s'ouvre sur place : sa vidéo, sa bascule, puis ses activités
 * (`<TutorLiveActivityList>`), sans menu ni onglet — le réflexe de l'ancien focus, où la liste d'un
 * seul élève se tenait à gauche du centre. Toucher une autre vidéo y amène sa liste.
 *
 * Les vidéos prennent la largeur de la pellicule, à un rapport fixe ; la hauteur de la pellicule, assez
 * pour une tablette en portrait (`TALL_FROM`), les agrandit et ouvre « Faites aujourd'hui » quand la
 * liste se monte. Ce qui ne tient pas défile dans la pellicule, jamais la page ; ma vidéo reste au pied.
 *
 * Round 3 (Wajih) : un élève qu'on ne regarde pas porte une petite barre, l'activité qu'il a devant lui
 * et son visage dessus (`<TutorStudentTile>`). Sous la liste de l'élève regardé revient, en un geste,
 * **l'historique de ses échanges avec le champion** (« … · n », `onOpenHelper`, l'ancien bouton de sa
 * tuile). « Ajouter une activité » et « Son profil » sont au centre.
 *
 * Contrôlée : l'élève regardé et l'activité ouverte au centre arrivent en props.
 */
import React, { useState } from "react"
import { ScrollView, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { elide } from "../../../ports/primary/ui/formatters/childFormatter"

import { type UILiveParticipant, type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Badge } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { TutorLiveActivityList } from "../../molecules/TutorLiveActivityList"
import { TutorStudentTile } from "../../molecules/TutorStudentTile"
import { tutorMeParticipant } from "../../molecules/TutorStudentVideo"
import { VideoTile } from "../../molecules/VideoTile"

/** Ce que ma vignette dit de moi : je présente le centre, ou je partage mon écran. */
export type TutorLiveMeState = "presenting" | "sharing"

export type TutorFilmstripProps = {
    students: UITutorLiveStudent[]
    selectedId?: string
    onSelect: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    /** L'élève dont la bascule du mode part. */
    modePendingId?: string
    /** L'activité ouverte au centre : sa ligne est teintée dans la liste. */
    openActivityId?: string
    onOpenActivity: (studentId: string, activityId: string) => void
    /** Réordonner « À faire » de l'élève regardé. Absent : pas de glisser. */
    onReorder?: (studentId: string, activityIds: string[]) => void
    /** « Voir plus » sous ses faites. Absent : toutes. */
    onSeeMoreDone?: (studentId: string) => void
    /** Ouvrir le fil du chat d'un élève (ses non-lus, sur sa vidéo). */
    onOpenChat?: (studentId: string) => void
    /** Le prénom du champion qui aide en séance (le monde de jeu de l'espace) : « … · n ». */
    helperName?: string
    /** L'historique de ses échanges avec le champion. */
    onOpenHelper?: (studentId: string) => void
    /** Replier la pellicule (le chevron de l'en-tête), pour élargir le centre. Absent : pas de chevron. */
    onCollapse?: () => void
    me: UILiveParticipant
    meState?: TutorLiveMeState
    /** La vraie piste d'un participant (`me.id` pour la mienne). Absente : sa photo, ou son visage. */
    renderVideo?: (participantId: string) => React.ReactNode
    testID?: string
}

const TEXTS = {
    title: (count: number) => `Élèves · ${count}`,
    presenting: "Tu présentes",
    sharing: "Écran partagé",
    helper: (name: string, count: number) => `${count} ${count === 1 ? "résumé" : "résumés"} ${elide(name)}`,
    collapse: "Replier les élèves",
}

/** À partir de cette hauteur (une tablette en portrait), les vidéos grandissent et « Faites » s'ouvre. */
const TALL_FROM = 900

/**
 * Le rapport largeur / hauteur des vidéos : l'élève regardé, les autres, moi ; plus hautes en portrait. Chaque
 * élève, regardé ou non, connecté ou non, a la même vignette en 16:9, où sa caméra tient entière (`contain`) :
 * à 3,3 et 2,2, les vignettes étaient de minces bandes où l'image se coupait (Wajih, live en prod du 03/11).
 * La mienne reste petite, sa caméra entière dedans.
 */
const ASPECT = {
    low: { selected: 16 / 9, other: 16 / 9, me: 2.4 },
    tall: { selected: 1.6, other: 1.6, me: 1.7 },
}

export function TutorFilmstrip(props: TutorFilmstripProps) {
    const {
        students,
        selectedId,
        onSelect,
        onChangeMode,
        modePendingId,
        openActivityId,
        onOpenActivity,
        onReorder,
        onSeeMoreDone,
        onOpenChat,
        helperName,
        onOpenHelper,
        onCollapse,
        me,
        meState,
        renderVideo,
        testID,
    } = props
    // État d'interface : la pellicule a-t-elle la hauteur d'une tablette en portrait ? Gardé en booléen : la
    // vue ne se redessine qu'au passage du seuil. `null` avant la mesure : la liste attend de savoir où
    // « Faites aujourd'hui » part (ouvert si la pellicule est haute), puis garde ce que le masteur en fait.
    const [tall, setTall] = useState<boolean | null>(null)
    const aspect = tall === true ? ASPECT.tall : ASPECT.low
    const meVideo = renderVideo?.(me.id)

    return (
        <View
            style={styles.root}
            onLayout={(event) => setTall(event.nativeEvent.layout.height >= TALL_FROM)}
            testID={testID}
        >
            <View style={styles.head}>
                <SectionLabel>{TEXTS.title(students.length)}</SectionLabel>
                {onCollapse != null && (
                    <IconButton
                        iconName="panel-left-close"
                        appearance="transparent"
                        iconColor="text-muted"
                        size={28}
                        iconSize={16}
                        onPress={onCollapse}
                        accessibilityLabel={TEXTS.collapse}
                    />
                )}
            </View>
            <ScrollView style={styles.scroll} contentContainerStyle={styles.list}>
                {students.map((student) => {
                    const selected = student.id === selectedId
                    return (
                        <TutorStudentTile
                            key={student.id}
                            student={student}
                            videoAspect={selected ? aspect.selected : aspect.other}
                            selected={selected}
                            onSelect={() => onSelect(student.id)}
                            onChangeMode={(mode) => onChangeMode(student.id, mode)}
                            modePending={student.id === modePendingId}
                            video={renderVideo?.(student.id)}
                            onOpenActivity={(activityId) => onOpenActivity(student.id, activityId)}
                            onOpenChat={onOpenChat != null ? () => onOpenChat(student.id) : undefined}
                        >
                            {selected && tall != null && (
                                <TutorLiveActivityList
                                    activities={student.activities}
                                    student={student}
                                    openActivityId={openActivityId}
                                    onOpenActivity={(activityId) => onOpenActivity(student.id, activityId)}
                                    onReorder={onReorder != null ? (ids) => onReorder(student.id, ids) : undefined}
                                    onSeeMoreDone={onSeeMoreDone != null ? () => onSeeMoreDone(student.id) : undefined}
                                    moreDone={student.moreDone}
                                    defaultDoneOpen={tall}
                                />
                            )}
                            {selected &&
                                helperName != null &&
                                onOpenHelper != null &&
                                (student.samSummaries ?? 0) > 0 && (
                                    <TextButton
                                        label={TEXTS.helper(helperName, student.samSummaries ?? 0)}
                                        size="sm"
                                        onPress={() => onOpenHelper(student.id)}
                                    />
                                )}
                        </TutorStudentTile>
                    )
                })}
            </ScrollView>
            <View style={styles.me(aspect.me)}>
                <VideoTile participant={tutorMeParticipant(me, meVideo)} size="tile" me video={meVideo} />
                {meState != null && (
                    <View style={styles.meBadge} pointerEvents="none">
                        <Badge
                            text={meState === "presenting" ? TEXTS.presenting : TEXTS.sharing}
                            variant="primary"
                            iconName={meState === "presenting" ? "chalkboard-teacher" : "desktop"}
                            truncate={false}
                        />
                    </View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, gap: theme.spacing["2"] },
    head: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", minHeight: 28 },
    scroll: { flex: 1, minHeight: 0 },
    list: { gap: theme.spacing["2"], paddingBottom: theme.spacing["1"] },
    me: (aspect: number) => ({ aspectRatio: aspect, borderRadius: theme.radius.md, overflow: "hidden" as const }),
    meBadge: { position: "absolute", top: theme.spacing["1"], left: theme.spacing["1"] },
}))
